import { useState, useRef, useEffect } from "react";
import { useUploadMedia } from "../../../../hooks/mutations/useMediaMutations";

interface VideoRecorderProps {
  onVideoReady: (videoBlob: Blob, videoURL: string, fileKey?: string) => void;
  maxDuration?: number; // Max recording duration in seconds (default 60)
  applicationId?: string;
  questionId?: string;
}

type RecorderState = "initial" | "recording" | "review" | "uploading";

export default function VideoRecorder({
  onVideoReady,
  maxDuration = 60,
  applicationId,
  questionId,
}: VideoRecorderProps) {
  const [state, setState] = useState<RecorderState>("initial");
  const [countdown, setCountdown] = useState(maxDuration);
  const [videoURL, setVideoURL] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const countdownIntervalRef = useRef<number | null>(null);

  const uploadMedia = useUploadMedia();

  // Request camera access on mount
  useEffect(() => {
    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: true,
        });
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (error) {
        console.error("Error accessing camera:", error);
        alert("Unable to access camera. Please allow camera permissions.");
      }
    };

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Update video source when stream changes
  useEffect(() => {
    if (videoRef.current && stream && state === "initial") {
      videoRef.current.srcObject = stream;
    }
  }, [stream, state]);

  const startRecording = () => {
    if (!stream) return;

    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(stream, { mimeType: "video/webm" });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setVideoURL(url);
      setState("review");

      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = url;
        videoRef.current.currentTime = 0;
        videoRef.current.pause();
      }

      // If we have applicationId/questionId, upload to S3
      if (applicationId) {
        setState("uploading");
        setUploadError(null);
        const file = new File([blob], "video-response.webm", { type: "video/webm" });
        uploadMedia.mutate(
          {
            request: {
              fileName: "video-response.webm",
              contentType: "video/webm",
              applicationId,
              questionId,
            },
            file,
          },
          {
            onSuccess: (result) => {
              setState("review");
              onVideoReady(blob, url, result.fileKey);
            },
            onError: (err) => {
              console.error("Video upload failed:", err);
              setUploadError("Upload failed. Your recording is saved locally.");
              setState("review");
              onVideoReady(blob, url);
            },
          }
        );
      } else {
        onVideoReady(blob, url);
      }
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setState("recording");
    setCountdown(maxDuration);

    // Start countdown
    countdownIntervalRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stopRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const handleRetake = () => {
    setVideoURL(null);
    setState("initial");
    setCountdown(maxDuration);
    setUploadError(null);

    if (videoRef.current && stream) {
      videoRef.current.src = "";
      videoRef.current.srcObject = stream;
    }
  };

  return (
    <div className="space-y-4">
      {/* Video Display */}
      <div className="relative bg-gray-900 rounded-2xl overflow-hidden aspect-video">
        <video
          ref={videoRef}
          autoPlay={state !== "review" && state !== "uploading"}
          playsInline
          controls={state === "review"}
          muted={state !== "review"}
          onEnded={() => {
            if (videoRef.current) {
              videoRef.current.currentTime = 0;
              videoRef.current.pause();
            }
          }}
          className="w-full h-full object-cover"
        />

        {/* Recording Indicator */}
        {state === "recording" && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-full">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
            <span className="font-bold text-sm">REC</span>
          </div>
        )}

        {/* Countdown Timer */}
        {state === "recording" && (
          <div className="absolute top-4 right-4 bg-black/70 text-white px-4 py-2 rounded-full font-mono text-lg font-bold">
            {countdown}s
          </div>
        )}

        {/* Uploading Overlay */}
        {state === "uploading" && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm font-medium">Uploading video...</p>
            </div>
          </div>
        )}

        {/* Initial Overlay */}
        {state === "initial" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="text-white text-center">
              <svg className="w-16 h-16 mx-auto mb-2 opacity-80" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
              </svg>
              <p className="text-sm font-medium">Ready to record</p>
            </div>
          </div>
        )}
      </div>

      {/* Upload Error */}
      {uploadError && (
        <p className="text-sm text-amber-600 text-center">{uploadError}</p>
      )}

      {/* Controls */}
      <div className="flex gap-3">
        {state === "initial" && (
          <button
            onClick={startRecording}
            className="flex-1 h-14 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <div className="w-4 h-4 bg-white rounded-full" />
            Start Recording
          </button>
        )}

        {state === "recording" && (
          <button
            onClick={stopRecording}
            className="flex-1 h-14 bg-gray-800 hover:bg-gray-900 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <div className="w-4 h-4 bg-white" />
            Stop Recording
          </button>
        )}

        {(state === "review" || state === "uploading") && (
          <button
            onClick={handleRetake}
            disabled={state === "uploading"}
            className="flex-1 h-14 border-2 border-gray-300 hover:border-gray-400 bg-white text-gray-700 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            Retake
          </button>
        )}
      </div>

      {/* Instructions */}
      <div className="text-center text-sm text-gray-600">
        {state === "initial" && <p>You'll have {maxDuration} seconds to record your response</p>}
        {state === "recording" && <p>Recording... Click stop when you're done</p>}
      </div>
    </div>
  );
}
